import { Tooltip } from "@artis/ui";
import { useEffect, useRef, useState } from "react";

type SheetInfo = { id: string; name: string };

type SheetTabsProps = {
  sheets: SheetInfo[];
  activeId: string;
  onSelect: (id: string) => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onRename: (id: string, name: string) => void;
  readOnly?: boolean;
};

const normalise = (name: string) => name.trim().toLowerCase();

export default function SheetTabs({ sheets, activeId, onSelect, onAdd, onDelete, onRename, readOnly }: SheetTabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [selectRequest, setSelectRequest] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const editingTabRef = useRef<HTMLDivElement>(null);

  const name = draft.trim();
  const clash =
    Boolean(editingId && name) && sheets.some((s) => s.id !== editingId && normalise(s.name) === normalise(name));

  useEffect(() => {
    if (!selectRequest) return;
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [selectRequest]);

  const startRename = (s: SheetInfo) => {
    setEditingId(s.id);
    setDraft(s.name);
  };

  const stopRename = () => {
    setEditingId(null);
    setSelectRequest(0);
  };

  const save = () => {
    if (!editingId) return;
    const current = sheets.find((s) => s.id === editingId)?.name;
    if (name && name !== current) onRename(editingId, name);
    stopRename();
  };

  const submit = () => {
    if (clash) setSelectRequest((n) => n + 1);
    else save();
  };

  return (
    <div
      data-testid="scratchpad-sheet-tabs"
      data-artis-ui
      className="flex h-[28px] w-full items-stretch border-[var(--colour-border-default)] border-t bg-[var(--colour-surface-variant)] px-1 text-[12px]"
    >
      {sheets.map((s) => {
        const active = s.id === activeId;
        const editing = editingId === s.id;
        return (
          <div
            key={s.id}
            ref={editing ? editingTabRef : undefined}
            data-testid="scratchpad-sheet-tab"
            data-sheet-name={s.name}
            data-active={active ? "true" : "false"}
            className={`flex cursor-pointer items-center gap-1 border-[var(--colour-border-default)] border-r px-2 ${active ? "bg-[var(--colour-surface-page-default)] text-[var(--colour-text-default)]" : "text-[var(--colour-text-light)] hover:bg-[var(--colour-surface-hover)]"}`}
            onClick={() => onSelect(s.id)}
            onDoubleClick={() => {
              if (!readOnly) startRename(s);
            }}
          >
            {editing ? (
              <input
                ref={inputRef}
                data-testid="scratchpad-sheet-rename"
                className="w-20 bg-transparent outline-none"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={clash ? stopRename : save}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                  if (e.key === "Escape") stopRename();
                }}
              />
            ) : (
              <span>{s.name}</span>
            )}
            {!readOnly && sheets.length > 1 && (
              <button
                type="button"
                data-testid="scratchpad-delete-sheet"
                aria-label={`Delete ${s.name}`}
                className="text-[var(--colour-text-lighter)] hover:text-[var(--colour-text-default)]"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(s.id);
                }}
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button
          type="button"
          data-testid="scratchpad-add-sheet"
          aria-label="Add sheet"
          className="px-2 text-[var(--colour-text-light)] hover:text-[var(--colour-text-default)]"
          onClick={onAdd}
        >
          +
        </button>
      )}
      {clash && (
        <Tooltip
          data-testid="scratchpad-sheet-rename-error"
          title="Please choose a unique name"
          anchor={editingTabRef.current}
        />
      )}
    </div>
  );
}
